import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const Services = () => {
  const headerRef = useRef(null);
  const servicesRef = useRef(null);
  const pricingRef = useRef(null);

  useEffect(() => {
    // Animation sobre d'entrée de la page
    const tl = gsap.timeline();
    
    tl.fromTo(headerRef.current.querySelector('h1'),
      { opacity: 0 },
      { opacity: 1, duration: 0.8, ease: "power2.out" }
    )
    .fromTo(headerRef.current.querySelector('.lead'),
      { opacity: 0 },
      { opacity: 1, duration: 0.6, ease: "power2.out" },
      "-=0.4"
    );

    // Animation sobre des cartes de services
    gsap.fromTo(servicesRef.current.querySelectorAll('.service-card'),
      { opacity: 0 },
      {
        opacity: 1,
        duration: 0.8,
        stagger: 0.15,
        ease: "power2.out",
        scrollTrigger: {
          trigger: servicesRef.current,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse"
        }
      }
    );

    // Animation sobre de la section tarifs
    gsap.fromTo(pricingRef.current.querySelectorAll('.pricing-item'),
      { opacity: 0 },
      {
        opacity: 1,
        duration: 0.8,
        stagger: 0.15,
        ease: "power2.out",
        scrollTrigger: {
          trigger: pricingRef.current,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse"
        }
      }
    );

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const services = [
    {
      title: 'Bilan Comportemental',
      description: 'Diagnostic complet à domicile ou en extérieur pour évaluer les besoins de votre chien et proposer un plan d\'action personnalisé.',
      image: '/services/pexels-bertellifotografia-1906153.jpg',
      features: [
        'Analyse complète du comportement',
        'Plan d\'action personnalisé',
        'Conseils pratiques',
        'Rapport détaillé'
      ],
      duration: '2h',
      price: '80€'
    },
    {
      title: 'Séances d\'Éducation',
      description: 'Séances individuelles ou forfaits de 3 à 5 séances pour l\'éducation de votre compagnon avec des méthodes respectueuses.',
      image: '/services/pexels-jagheterjohann-1254140.jpg',
      features: [
        'Séances individuelles',
        'Forfaits disponibles',
        'Méthodes positives',
        'Suivi personnalisé'
      ],
      duration: '1h par séance',
      price: '55€'
    },
    {
      title: 'Forfait 3 cours',
      description: 'Suivi régulier pour consolider les apprentissages (3 × 1h).',
      image: '/services/pexels-albinawhite-11059893.jpg',
      features: [
        '3 séances individuelles',
        'Programme personnalisé',
        'Suivi et ajustements'
      ],
      duration: '3 × 1h',
      price: '150€'
    },
    {
      title: 'Forfait 5 cours',
      description: 'Accompagnement étalé dans le temps pour des résultats durables (5 × 1h).',
      image: '/services/pexels-valeriya-1805164.jpg',
      features: [
        '5 séances individuelles',
        'Objectifs progressifs',
        'Bilan d\'étape'
      ],
      duration: '5 × 1h',
      price: '240€'
    },
    {
      title: 'Rééducation Comportementale',
      description: 'Accompagnement spécialisé pour corriger les troubles comportementaux et améliorer le bien-être de votre chien.',
      image: '/services/pexels-jozef-feher-356581-1633522.jpg',
      features: [
        'Traitement des phobies',
        'Gestion de l\'agressivité',
        'Anxiété de séparation',
        'Suivi à long terme'
      ],
      duration: 'Variable selon besoins',
      price: 'Sur devis'
    },
    {
      title: 'Balades Collectives',
      description: 'Balades encadrées en groupe pour socialiser votre chien dans un environnement sécurisé et convivial.',
      image: '/services/pexels-miami302-23383655.jpg',
      features: [
        'Socialisation encadrée',
        'Groupes adaptés',
        'Exercice physique',
        'Apprentissage collectif'
      ],
      duration: '1h',
      price: '15€'
    },
    {
      title: 'Ateliers Détection d\'Odeurs',
      description: 'Activités ludiques de recherche olfactive pour stimuler l\'intelligence naturelle de votre chien.',
      image: '/services/pexels-dom-bucci-281808-981062.jpg',
      features: [
        'Stimulation mentale',
        'Développement olfactif',
        'Activité ludique',
        'Renforcement positif'
      ],
      duration: '45min - 1h',
      price: '30€'
    },
    {
      title: 'Tricks Funs',
      description: 'Séances de jeux éducatifs et ludiques pour apprendre des tours amusants tout en renforçant le lien avec votre chien.',
      image: '/services/pexels-dsd-143941-975413.jpg',
      features: [
        'Apprentissage de tours',
        'Renforcement du lien',
        'Stimulation mentale',
        'Moments de complicité'
      ],
      duration: '1h',
      price: '20€'
    },
    {
      title: 'Services de Balades',
      description: 'Dépenser et stimuler votre chien pendant vos journées chargées, en toute confiance.',
      image: '/services/pexels-carolin-wenske-762365559-20620968.jpg',
      features: [
        '30 min ou 1h',
        'Quartier, parc ou forêt',
        'Routine adaptée au chien',
        'Compte-rendu si souhaité'
      ],
      duration: '30min / 1h',
      price: '12€ / 20€ (Abonnement 5×1h : 90€)'
    },
    {
      title: 'Garde à domicile / visites',
      description: 'Je passe à votre domicile pour m’occuper de votre compagnon avec attention.',
      image: '/services/Sky.JPG',
      features: [
        'Sortie hygiène / balade',
        'Jeux, câlins, stimulation mentale',
        'Eau et nourriture',
        'Compte-rendu quotidien'
      ],
      duration: '1h / 2 visites / 5 jours',
      price: '1h : 20€ • 2 visites/jour : 35€ • 5 jours (2/jour) : 160€'
    },
    {
      title: 'Garde de Jour (à venir)',
      description: 'Accueil et garde de votre chien en journée, avec activités et socialisation.',
      image: '/services/20250501_142617 (1).jpg',
      features: [
        'Accueil en journée',
        'Activités adaptées',
        'Socialisation encadrée',
        'Suivi personnalisé'
      ],
      duration: 'Journée',
      price: 'À venir'
    }
  ];

  const pricingInfo = [
    {
      title: 'Modalités de Paiement',
      items: [
        'Paiement lors du premier rendez-vous',
        'Espèces, chèque ou virement bancaire acceptés',
        'Échelonnement possible sur demande',
        'Devis gratuit sur demande'
      ]
    },
    {
      title: 'Validité des Forfaits',
      items: [
        'Forfaits valables 4 mois',
        'À compter du premier rendez-vous',
        'Possibilité de report en cas de force majeure',
        'Remboursement selon conditions CGV'
      ]
    },
    {
      title: 'Conditions d\'Annulation',
      items: [
        'Préavis de 24h pour séances individuelles',
        'Préavis de 48h pour activités collectives',
        'Facturation en cas d\'annulation tardive',
        'Report possible selon disponibilités'
      ]
    },
    {
      title: 'Informations complémentaires',
      items: [
        'Zone d\'intervention : Oullins et alentours (ouest lyonnais)',
        'Frais de déplacement : 0,50 €/km au-delà de 15 km'
      ]
    }
  ];

  return (
    <div className="services-page">
      {/* Header */}
      <section ref={headerRef} className="hero">
        <div className="container">
          <div className="hero-content">
            <h1>Mes Services</h1>
            <p className="lead">
              Découvrez ma gamme complète de services d'éducation et de rééducation canine<br />
              adaptés aux besoins spécifiques de votre compagnon
            </p>
          </div>
        </div>
      </section>

      {/* Services détaillés */}
      <section ref={servicesRef} className="section">
        <div className="container">
          <div className="grid grid-2">
            {services.map((service, index) => (
              <div key={index} className="service-card card">
                <div className="service-image">
                  <img src={service.image} alt={service.title} />
                </div>
                <div className="card-header">
                  <h3>{service.title}</h3>
                  {/* Badges retirés à la demande de la cliente */}
                </div>
                
                <div className="card-body">
                  <p>{service.description}</p>
                  
                  <h4>Ce qui est inclus :</h4>
                  <ul>
                    {service.features.map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>
                </div>
                
                <div className="card-footer">
                  <div className="service-meta">
                    <div className="service-duration">
                      <strong>Durée :</strong> {service.duration}
                    </div>
                    <div className="service-price">
                      <strong>Tarif :</strong> {service.price}
                    </div>
                  </div>
                  <Link to="/contact" className="btn btn-primary" style={{ width: '100%', marginTop: 'var(--spacing-sm)' }}>
                    Demander un devis
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Conditions de participation */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header">
            <h2>Conditions de Participation</h2>
            <p className="lead">
              Pour assurer la sécurité et le bien-être de tous, voici les conditions requises
            </p>
          </div>
          
          <div className="grid grid-4">
            <div className="card text-center">
              <div className="service-icon" style={{ fontSize: '2rem', margin: '0 auto var(--spacing-sm)' }}>
                🆔
              </div>
              <h4>Identification</h4>
              <p>Chien identifié par puce électronique ou tatouage</p>
            </div>
            
            <div className="card text-center">
              <div className="service-icon" style={{ fontSize: '2rem', margin: '0 auto var(--spacing-sm)' }}>
                💉
              </div>
              <h4>Vaccinations</h4>
              <p>Vaccinations à jour selon le protocole vétérinaire</p>
            </div>
            
            <div className="card text-center">
              <div className="service-icon" style={{ fontSize: '2rem', margin: '0 auto var(--spacing-sm)' }}>
                🦠
              </div>
              <h4>Antiparasitaires</h4>
              <p>Traitement antiparasitaire récent et efficace</p>
            </div>
            
            <div className="card text-center">
              <div className="service-icon" style={{ fontSize: '2rem', margin: '0 auto var(--spacing-sm)' }}>
                🎂
              </div>
              <h4>Âge minimum</h4>
              <p>Chien âgé d'au moins 3 mois pour tous mes services</p>
            </div>
          </div>
        </div>
      </section>

      {/* Informations pratiques */}
      <section ref={pricingRef} className="section">
        <div className="container">
          <div className="section-header">
            <h2>Informations Pratiques</h2>
            <p className="lead">
              Tout ce que vous devez savoir sur mes tarifs et conditions
            </p>
          </div>
          
          <div className="grid grid-3">
            {pricingInfo.map((info, index) => (
              <div key={index} className="pricing-item card">
                <h3>{info.title}</h3>
                <ul>
                  {info.items.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-xl">
            <div className="card" style={{ display: 'inline-block', padding: 'var(--spacing-lg)', background: 'var(--gradient-warm)', color: 'var(--white)' }}>
              <h3 style={{ color: 'var(--white)', marginBottom: 'var(--spacing-sm)' }}>
                Prête à commencer ?
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.9)', marginBottom: 'var(--spacing-md)' }}>
                Contactez-moi pour un bilan personnalisé
              </p>
              <Link to="/contact" className="btn btn-outline" style={{ color: 'var(--white)', borderColor: 'var(--white)' }}>
                Obtenir un devis gratuit
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p>&copy; 2024 Atout Canin - Éducation et Rééducation Canine. Tous droits réservés.</p>
          <p>
            <Link to="/cgv">Conditions Générales de Vente</Link>
            <Link to="/contact">Mentions Légales</Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Services; 